import { getServices } from "../../api/getServices";
import {
  showAcceleratorUnusedModal,
  showMoreRecentCollaboratorVersionModal,
} from "../redux/actions/modal";
import {
  setDataSourceReferences,
  setShowKernelForkedFromPaidTierWarning,
  setStartedCanceled,
} from "../redux/actions/notebook";
import { SetDraft } from "../types/DraftSpec";

interface GetSessionInfoResult {
  runId: number;
  sessionTimeoutSeconds: number;
  isCanceled: boolean;
}

const getOrCreateSession = async (kernelId: number) => {
  try {
    return await getServices().kernelsClient.getOrCreateKernelSession({
      kernelId,
    });
  } catch (e) {
    throw new Error(e.message || "Failed to get kernel session");
  }
};

export const getSessionInfo = async (
  kernelId: number,
  setDraft: SetDraft
): Promise<GetSessionInfoResult> => {
  const sessionInfo = await getOrCreateSession(kernelId);
  if (!sessionInfo || !sessionInfo.draft) {
    throw new Error("Failed to get kernel session.");
  }

  const isCanceled = ["cancelAcknowledged", "cancelRequested"].includes(sessionInfo?.sessionStatus || "");
  setStartedCanceled(isCanceled);
  setDraft(kernelId, sessionInfo.draft);
  setDataSourceReferences(sessionInfo.draft.blob?.settings?.dataSources || []);

  if (!sessionInfo.sessionId) {
    throw new Error(
      sessionInfo.sessionCreationFailureMessage ||
        "Failed to start a new session"
    );
  }

  if (sessionInfo.unusedAcceleratorRemoved) {
    showAcceleratorUnusedModal(sessionInfo.unusedAcceleratorRemoved);
  } else if (sessionInfo.hasMoreRecentCollaboratorVersion) {
    // Only 1 modal can show at a time, and the GPU/TPU modal takes
    // precedence.  We'll just skip this informational modal if we
    // have to show the AcceleratorUnusedModal.
    showMoreRecentCollaboratorVersionModal();
  }

  setShowKernelForkedFromPaidTierWarning(
    sessionInfo.kernelForkedFromPaidTierWarning
  );

  return {
    runId: sessionInfo.sessionId,
    sessionTimeoutSeconds: sessionInfo.sessionTimeoutSeconds,
    isCanceled,
  };
};
